/**
 * LinearProgress Component
 *
 * A horizontal progress bar with customizable variants, animations,
 * and optional label display.
 *
 * @example
 *```tsx
 * <LinearProgress value={75} />
 * <LinearProgress value={50} variant="gradient"showLabel />
 * <LinearProgress indeterminate />
 *```
 */

import * as React from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import {linearProgressVariants, progressBarVariants} from './LinearProgress.variants';

// ============================================================================
// TYPES
// ============================================================================

interface LinearProgressProps
 extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
 VariantProps<typeof linearProgressVariants> {
 /** Progress value from 0 to 100 */
 value?: number;
 /** Bar style: solid, gradient, or striped */
 barStyle?: 'solid' | 'gradient' | 'striped';
 /** Whether to show the percentage label */
 showLabel?: boolean;
 /** Label position */
 labelPosition?: 'inside' | 'outside' | 'above';
 /** Custom label (overrides percentage) */
 label?: string;
 /** Whether to animate the progress bar */
 animated?: boolean;
 /** Indeterminate loading state */
 indeterminate?: boolean;
 /** Custom bar className */
 barClassName?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

const LinearProgress = React.forwardRef<HTMLDivElement, LinearProgressProps>(
 (
 {
 className,
 value = 0,
 variant = 'default',
 size = 'md',
 barStyle = 'solid',
 showLabel = false,
 labelPosition = 'outside',
 label,
 animated = true,
 indeterminate = false,
 barClassName,
 ...props
 },
 ref
 ) => {
 // Clamp value between 0 and 100
 const clampedValue = Math.min(100, Math.max(0, value));

 // Label font size based on size
 const labelSizeClass = {
 xs: 'text-[8px]',
 sm: 'text-micro',
 md: 'text-xs',
 lg: 'text-sm',
 xl: 'text-base',
 }[size ?? 'md'];

 // Can only show inside label for lg/xl sizes
 const canShowInsideLabel = ['lg', 'xl'].includes(size ?? 'md');
 const effectiveLabelPosition =
 labelPosition === 'inside' && !canShowInsideLabel ? 'outside' : labelPosition;

 const progressBar = (
 <div
 className={cn(
 progressBarVariants({ variant, style: barStyle }),
 indeterminate && 'animate-progress-indeterminate',
 barStyle === 'striped' && animated && 'animate-progress-stripes',
 barClassName
 )}
 style={{
 width: indeterminate ? '40%' :`${clampedValue}%`,
 }}
 >
 {/* Inside label */}
 {showLabel && effectiveLabelPosition === 'inside' && !indeterminate && (
 <span
 className={cn(
 'absolute inset-0 flex items-center justify-center font-medium text-primary-foreground',
 labelSizeClass
 )}
 >
 {label ??`${Math.round(clampedValue)}%`}
 </span>
 )}
 </div>
 );

 // Above label layout
 if (showLabel && effectiveLabelPosition === 'above' && !indeterminate) {
 return (
 <div className="w-full">
 <div className={cn('flex justify-between mb-1', labelSizeClass)}>
 <span className="text-[hsl(var(--text-muted))]">Progress</span>
 <span className="font-medium text-[hsl(var(--text-primary))]">
 {label ??`${Math.round(clampedValue)}%`}
 </span>
 </div>
 <div
 ref={ref}
 className={cn(linearProgressVariants({ size, variant }), className)}
 role="progressbar"
 aria-valuenow={clampedValue}
 aria-valuemin={0}
 aria-valuemax={100}
 aria-label={`${clampedValue}% complete`}
 {...props}
 >
 {progressBar}
 </div>
 </div>
 );
 }

 // Outside label layout
 if (showLabel && effectiveLabelPosition === 'outside' && !indeterminate) {
 return (
 <div className="flex items-center gap-3 w-full">
 <div
 ref={ref}
 className={cn(linearProgressVariants({ size, variant }), 'flex-1', className)}
 role="progressbar"
 aria-valuenow={clampedValue}
 aria-valuemin={0}
 aria-valuemax={100}
 aria-label={`${clampedValue}% complete`}
 {...props}
 >
 {progressBar}
 </div>
 <span className={cn('font-medium text-[hsl(var(--text-primary))] min-w-[3ch]', labelSizeClass)}>
 {label ??`${Math.round(clampedValue)}%`}
 </span>
 </div>
 );
 }

 // Default layout (no label or inside label)
 return (
 <div
 ref={ref}
 className={cn(linearProgressVariants({ size, variant }), className)}
 role="progressbar"
 aria-valuenow={indeterminate ? undefined : clampedValue}
 aria-valuemin={0}
 aria-valuemax={100}
 aria-label={indeterminate ? 'Loading' :`${clampedValue}% complete`}
 {...props}
 >
 {progressBar}
 </div>
 );
 }
);

LinearProgress.displayName = 'LinearProgress';

export { LinearProgress };
